/**
 * 파일명 : evl0109p.tsx
 * 설명 : 인사평가 평가자 수정 팝업
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
/* React */
import React from 'react';


/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Evl0109pAction } from '../../../redux/slice/evl/evl0109p_slice';
import { IInquiryCndsState, ITargetItemState, IUserItemState, IDeptTreeState } from '../../../redux/models/evl/evl0109p_model';

/* AntD */
import { Layout, Row, Col, Space, Button, Tree, Table, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { GetComponentProps } from 'rc-table/lib/interface';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

/************************************************
 * 전역변수 선언영억
 ************************************************/
const Evl0109p = (props:any) => {
  /** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const deptTree   = useAppSelector((state) => state.Evl0109p.deptTree);
    const treeKey    = useAppSelector((state) => state.Evl0109p.treeKey);
    const maxLvlCd   = useAppSelector((state) => state.Evl0109p.maxLvlCd);
    const targetList = useAppSelector((state) => state.Evl0109p.targetList);
    const userList   = useAppSelector((state) => state.Evl0109p.userList);
    const eeEmpNo: string = props?.data?.eeEmpNo;
    const eeUserNm: string = props?.data?.eeUserNm;

;



    let myDeptNode:IDeptTreeState;
    const nodeRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});


    /************************************************
     * 화면 초기화 이벤트
     ************************************************/
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        dispatch(Evl0109pAction.setTargetList([]));

        if (!deptTree || deptTree.length < 1) {
            // 입력파라미터
            const requestData = {};
    
            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd : 'TRCOMR0003',
                success : (responseData:any) => {
                    dispatch(Evl0109pAction.setDeptTree(responseData.deptTree));
                    dispatch(Evl0109pAction.setMaxLvlCd(responseData.lastLvl));
    
    
                    // ✅ 피평가자 = 부모에서 전달받은 값
                    const target:ITargetItemState = {
                        dnm    : '피평가자',
                        apprEmpNo  : eeEmpNo,
                        apprUserNm : eeUserNm,
                    };
                    dispatch(Evl0109pAction.setTargetList([target]));
                },
            });
        } else {
            // ✅ 피평가자 = 부모에서 전달받은 값
            const target:ITargetItemState = {
                dnm    : '피평가자',
                apprEmpNo  : eeEmpNo,
                apprUserNm : eeUserNm,
            };
            dispatch(Evl0109pAction.setTargetList([target]));
        }
    }, []);

    const findNodeMyDept = (treeData:IDeptTreeState[]) => {
        for (const node of treeData) {
            if (node.key === common.getDeptCd()) return node;
      
            if (node.children) {
                const found:IDeptTreeState = findNodeMyDept(node.children);

                if (found) return found;
            }
        }
        return null;
    };

    React.useEffect(() => {
        if (deptTree && deptTree.length > 0 && (!myDeptNode || Object.keys(myDeptNode).length < 1)) {
            myDeptNode = findNodeMyDept(deptTree);

            if (myDeptNode) {
                setDeptUsers(myDeptNode.key);
            }
        }
    }, [deptTree]);


    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btnClose': {
                props.cancel();
                break;
            }
            case 'btnOk': {
                // ✅ 피평가자(eeEmpNo)는 제외하고 평가자만 반환
                props.accept(targetList.filter((item:ITargetItemState) => {
                    return item.apprEmpNo !== eeEmpNo;
                }));
                break;
            }
            default:
        }
    };

    /**
     * 기능 : Tree 선택 이벤트 선언
     * param : 
     */
    const onSelect = (_keys:any, event:any) => {
        setDeptUsers(event.node.key);
    };

    const setDeptUsers = (deptCd:string) => {
        const requestData:IInquiryCndsState = {
            deptCd : deptCd,
            userStCd : 'A',
        };
        
        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0003',
            success : (responseData:any) => {
                dispatch(Evl0109pAction.setUserList(responseData.userList));
                dispatch(Evl0109pAction.setTreeKey(deptCd));

                if (deptCd && nodeRefs.current[deptCd]) {
                    nodeRefs.current[deptCd].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            },
        });
    }

    /**
     * 기능 : onRow
     */
    const onUserRow: GetComponentProps<IUserItemState> = (record: IUserItemState, _rowIndex) => ({
    onClick: (_event: any) => {
        // ✅ 피평가자(eeEmpNo) 본인 선택 방지
        const isSelf = record.empNo === eeEmpNo;
        if (isSelf) return; 

        // ✅ base = 피평가자 1명만 유지
        const base = (targetList ?? []).filter((t: ITargetItemState) => t.apprEmpNo === eeEmpNo);

        // ✅ 평가자 순번은 (현재 평가자 수 + 1)
        const evlCount = (targetList ?? []).filter((t: ITargetItemState) => t.apprEmpNo !== eeEmpNo).length;

        const itemTarget: ITargetItemState = {
        dnm: "평가자",
        ord: 1, // 단일이므로 고정해도 됨
        apprEmpNo: record.empNo,
        apprUserNm: record.userNm,
        apprOrd: evlCount + 1, // 평가자 순번
        postNm: record.postNm,
        dutyNm: record.dutyNm,
        deptNm: record.deptNm,
        apprStNm: "대기",
        };

        dispatch(Evl0109pAction.setTargetList([...base, itemTarget]));
    },
    });

    const onTargetRow: GetComponentProps<ITargetItemState> = (record:ITargetItemState, _rowIndex) => ({
        onClick: (_event:any) => {
            // ✅ 피평가자(eeEmpNo)는 삭제 불가
            if (record.apprEmpNo === eeEmpNo) {
                return;
            }
            const arrFilterd:ITargetItemState[] = targetList.filter((item:ITargetItemState) => {
                return item.apprEmpNo !== record.apprEmpNo;
            });

            dispatch(Evl0109pAction.setTargetList(arrFilterd));
        },
    });

    /************************************************
     * 개발자함수 영역
     ************************************************/


    /************************************************
     * Render 변수 선언 및 셋팅영역
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */


     /* Render 변수 선언영역 */
     /* AntD */
     const { Content } = Layout;
     const { Text }    = Typography;

    /* Table Columns */
    const columnsUsers = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직책</div>,
                dataIndex: 'dutyNm',
                key: 'dutyNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>이름</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: '10%',
            }];
            return arr;
    }, []);

    const columnsTarget = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>구분</div>,
                dataIndex: 'dnm',
                key: 'dnm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>이름</div>,
                dataIndex: 'apprUserNm',
                key: 'apprUserNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>삭제</div>,
                key: 'action',
                render: (_text:string, record:ITargetItemState) => {
                    // ✅ 피평가자(eeEmpNo)는 삭제 버튼 숨김
                    if (record.apprEmpNo !== eeEmpNo) {
                        return (
                            <Button
                                id='btn_showDetail'
                                name='btn_showDetail'
                                className='link-button'
                                size='small'
                                icon={<DeleteOutlined style={{color:'red'}}/>}
                                onClick={() => {
                                    let arrTarget = [...targetList];
                                    arrTarget = arrTarget.filter((item:ITargetItemState) => {
                                        return item.apprEmpNo !== record.apprEmpNo;
                                    });
                                    dispatch(Evl0109pAction.setTargetList(arrTarget));
                                }}
                            />
                        )
                    } else {
                        return null;
                    }
                },
                align: 'center',
                width: '5%',
            }];
            return arr;
    }, []);


    return (
        /* 화면 디자인 영역*/
        <Layout>
            <Content>
                <Row>
                    <Col style={{width : 7 * parseInt(maxLvlCd) + '%', height : '500px'}}>
                        {deptTree && deptTree.length > 0 ?
                        <Tree
                            showLine
                            prefixCls='dept-tree'
                            className='dept-tree'
                            defaultExpandAll={true}
                            onSelect={onSelect}
                            treeData={deptTree}
                            selectedKeys={[treeKey]}
                            style={{maxHeight : '500px', overflowY : 'auto'}}
                            titleRender={(nodeData) => (
                                <span ref={(el) => {
                                    if (el) {
                                        nodeRefs.current[nodeData.key] = el;
                                    }
                                }}>
                                    {nodeData.title}
                                </span>
                              )}
                        /> : null}
                    </Col>
                    <Col style={{ width : 100 - 7 * parseInt(maxLvlCd) + '%', height : '500px'}}>
                        <Row>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>사용자목록</Text>
                            </Space>
                            <Col>
                                <Table
                                    dataSource={userList}
                                    key='empNo'
                                    columns={columnsUsers()}
                                    size='small'
                                    style={{width:'100%'}}
                                    scroll={{y:180}}
                                    pagination={false}
                                    onRow={onUserRow}
                                />
                            </Col>
                        </Row>
                        <br/>
                        <Row>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>평가자목록</Text>
                            </Space>
                            <Col>
                                <Table
                                    dataSource={targetList}
                                    key='apprEmpNo'
                                    columns={columnsTarget()}
                                    size='small'
                                    style={{width:'100%'}}
                                    scroll={{y:180}}
                                    pagination={false}
                                    onRow={onTargetRow}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row justify='center'>
                    <Col>
                        <Space direction='vertical'>
                            <Row justify='space-around' style={{ marginTop: 20 }}>
                                <Space size='small'>
                                    <Button
                                        name='btnClose'
                                        className='background-color-default-button'
                                        onClick={onClick}
                                    >
                                        닫기
                                    </Button>
                                    <Button
                                        name='btnOk'
                                        className='background-color-primary-button'
                                        onClick={onClick}
                                    >
                                        확인
                                    </Button>
                                </Space>
                            </Row>
                        </Space>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
}

export default Evl0109p;
